import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Textarea,
  Spinner,
  Badge,
  Tooltip,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { TeamRequest, RequestStatus, AppSettings, QuotaModel } from "../../types";

interface RequestDetailsDialogProps {
  selectedRequest: TeamRequest | null;
  approvalAction: "approve" | "reject" | null;
  comments: string;
  processingAction: "approve" | "reject" | null;
  isApprover: boolean;
  currentUserId: string;
  quotaModels: QuotaModel[];
  settings: AppSettings | null;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onCancelClick: (requestId: string) => void;
  onCommentsChange: (value: string) => void;
}

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
    width: "90%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%"
  },
  dialogTitle: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    marginBottom: tokens.spacingVerticalL,
    flexShrink: 0
  },
  dialogContent: {
    padding: tokens.spacingVerticalL,
    minHeight: 0,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto"
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    marginBottom: tokens.spacingVerticalXL,
    "@media (min-width: 650px)": {
      gridTemplateColumns: "repeat(2, 1fr)"
    }
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS
  },
  detailLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200
  },
  detailValue: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300
  },
  approvalSection: {
    marginTop: tokens.spacingVerticalXL,
    paddingTop: tokens.spacingVerticalXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`
  },
  approvalTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalM
  },
  commentInput: {
    marginBottom: tokens.spacingVerticalL
  },
  commentLabel: {
    display: "block",
    marginBottom: tokens.spacingVerticalS,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  commentTextarea: {
    width: "100%"
  },
  dialogActions: {
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    marginTop: "auto",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0
  },
  dialogButton: {
    minWidth: "80px"
  },
  approveButton: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    "&:hover": {
      backgroundColor: tokens.colorPaletteGreenBackground3
    }
  },
  rejectButton: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    "&:hover": {
      backgroundColor: tokens.colorPaletteRedBackground3
    }
  },
  cancelButton: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
    "&:hover": {
      backgroundColor: tokens.colorPaletteYellowBackground3
    }
  },
  approvalResponseContainer: {
    display: "flex",
    alignItems: "center",
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`
  },
  approvalResponseApproved: {
    backgroundColor: tokens.colorPaletteGreenBackground1
  },
  approvalResponseRejected: {
    backgroundColor: tokens.colorPaletteRedBackground1
  },
  approvalResponsePending: {
    backgroundColor: tokens.colorNeutralBackground3
  },
  approvalResponseNotNeeded: {
    backgroundColor: tokens.colorNeutralBackground2
  },
  approvalResponderInfo: {
    flex: 1
  },
  approvalResponderName: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold
  },
  approvalResponderDetails: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    marginLeft: "26px"
  },
  approvalResponderComments: {
    marginTop: tokens.spacingVerticalXS,
    fontStyle: "italic",
    color: tokens.colorNeutralForeground2
  }
});

export const RequestDetailsDialog = ({
  selectedRequest,
  approvalAction,
  comments,
  processingAction,
  isApprover,
  currentUserId,
  quotaModels,
  onClose,
  onApprove,
  onReject,
  onCancelClick,
  onCommentsChange
}: RequestDetailsDialogProps) => {
  const { t } = useTranslation("requestsPage");
  const styles = useStyles();

  const getPrivacyLabel = (privacy: string) => {
    const labelKeys: Record<string, string> = {
      private: "privacyLabels.private",
      public: "privacyLabels.public"
    };
    return t(labelKeys[privacy] || "privacyLabels." + privacy);
  };

  const getStatusBadge = (status: RequestStatus) => {
    const colors: Record<RequestStatus, "success" | "danger" | "warning" | "informative" | "subtle"> = {
      [RequestStatus.Pending]: "warning",
      [RequestStatus.Approved]: "success",
      [RequestStatus.Rejected]: "danger",
      [RequestStatus.Created]: "success",
      [RequestStatus.CreatingTeam]: "informative",
      [RequestStatus.Completed]: "success",
      [RequestStatus.Failed]: "danger",
      [RequestStatus.Cancelled]: "subtle"
    };

    const labelKeys: Record<RequestStatus, string> = {
      [RequestStatus.Pending]: "statusBadges.pending",
      [RequestStatus.Approved]: "statusBadges.approved",
      [RequestStatus.Rejected]: "statusBadges.rejected",
      [RequestStatus.Created]: "statusBadges.created",
      [RequestStatus.CreatingTeam]: "statusBadges.creatingTeam",
      [RequestStatus.Completed]: "statusBadges.completed",
      [RequestStatus.Failed]: "statusBadges.failed",
      [RequestStatus.Cancelled]: "statusBadges.cancelled"
    };

    return (
      <Badge appearance="filled" color={colors[status]} style={{ whiteSpace: "nowrap" }}>
        {t(labelKeys[status])}
      </Badge>
    );
  };

  return (
    <Dialog
      open={!!selectedRequest}
      onOpenChange={(_, { open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle className={styles.dialogTitle}>
            {approvalAction === "approve" && t("modal.approveTitle")}
            {approvalAction === "reject" && t("modal.rejectTitle")}
            {!approvalAction && t("modal.detailsTitle")}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {selectedRequest && (
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{t("modal.labels.teamName")}</div>
                  <div className={styles.detailValue}>{selectedRequest.teamName}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{t("modal.labels.type")}</div>
                  <div className={styles.detailValue}>{getPrivacyLabel(selectedRequest.privacy)}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{t("modal.labels.requester")}</div>
                  <div className={styles.detailValue}>{selectedRequest.requesterEmail}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{t("modal.labels.email")}</div>
                  <div className={styles.detailValue}>{selectedRequest.requesterEmail}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{t("modal.labels.status")}</div>
                  <div className={styles.detailValue}>{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>{t("modal.labels.createdDate")}</div>
                  <div className={styles.detailValue}>
                    {new Date(selectedRequest.submittedDate).toLocaleDateString("fr-FR")}
                  </div>
                </div>
                {selectedRequest.quotaModelId && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.quota")}</div>
                    <div className={styles.detailValue}>
                      {(() => {
                        const model = quotaModels.find((m) => m.id === selectedRequest.quotaModelId);
                        if (model) {
                          return `${model.name} (${model.maxStorageGB} GB)`;
                        }
                        return selectedRequest.quotaModelId;
                      })()}
                    </div>
                  </div>
                )}
                {selectedRequest.sensitivityLabelName && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.sensitivityLabel", "Étiquette de sensibilité")}</div>
                    <div className={styles.detailValue} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#0078D4',
                          display: 'inline-block',
                          flexShrink: 0
                        }}
                      />
                      <span>{selectedRequest.sensitivityLabelName}</span>
                    </div>
                  </div>
                )}
                {selectedRequest.description && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.description")}</div>
                    <div className={styles.detailValue}>{selectedRequest.description}</div>
                  </div>
                )}
                {selectedRequest.justification && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.justification")}</div>
                    <div className={styles.detailValue}>{selectedRequest.justification}</div>
                  </div>
                )}
                {selectedRequest.approverComments && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.approverComments")}</div>
                    <div className={styles.detailValue}>{selectedRequest.approverComments}</div>
                  </div>
                )}
                {selectedRequest.approverName && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.processedBy")}</div>
                    <div className={styles.detailValue}>{selectedRequest.approverName}</div>
                  </div>
                )}
                {selectedRequest.processedDate && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.processedDate")}</div>
                    <div className={styles.detailValue}>
                      {new Date(selectedRequest.processedDate).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                )}

                {/* Informations sur l'équipe Teams créée */}
                {selectedRequest.createdTeamId && (
                  <>
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>{t("modal.labels.teamsStatus")}</div>
                      <div className={styles.detailValue}>
                        {selectedRequest.status === RequestStatus.Failed ? (
                          <Badge appearance="tint" color="danger" size="large">
                            {t("modal.labels.teamsFailure")}
                          </Badge>
                        ) : (
                          <Badge appearance="tint" color="success" size="large">
                            {t("modal.labels.teamsSuccess")}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>{t("modal.labels.teamsId")}</div>
                      <div
                        className={styles.detailValue}
                        style={{
                          fontFamily: "monospace",
                          fontSize: tokens.fontSizeBase200,
                          wordBreak: "break-all"
                        }}
                      >
                        {selectedRequest.createdTeamId}
                      </div>
                    </div>
                  </>
                )}

                {/* Message pour les demandes en attente */}
                {!selectedRequest.createdTeamId && selectedRequest.status === RequestStatus.Approved && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.teamsStatus")}</div>
                    <div className={styles.detailValue}>
                      <Badge appearance="tint" color="warning" size="large">
                        {t("modal.labels.teamsPending")}
                      </Badge>
                    </div>
                  </div>
                )}

                {!selectedRequest.createdTeamId && selectedRequest.status === RequestStatus.Pending && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.teamsStatus")}</div>
                    <div className={styles.detailValue}>
                      <Badge appearance="outline" size="large">
                        {t("modal.labels.teamsApproval")}
                      </Badge>
                    </div>
                  </div>
                )}

                {selectedRequest.errorMessage && (
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>{t("modal.labels.errorMessage")}</div>
                    <div
                      className={styles.detailValue}
                      style={{
                        color: tokens.colorPaletteRedForeground1,
                        backgroundColor: tokens.colorPaletteRedBackground1,
                        padding: tokens.spacingVerticalM,
                        borderRadius: tokens.borderRadiusMedium,
                        border: `1px solid ${tokens.colorPaletteRedBorder1}`
                      }}
                    >
                      {selectedRequest.errorMessage}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Section de suivi des approbations */}
            {selectedRequest?.approvalResponses && selectedRequest.approvalResponses.length > 0 && (
              <div className={styles.approvalSection}>
                <div className={styles.approvalTitle}>{t("modal.approvalsSection.title")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM }}>
                  {selectedRequest.approvalResponses.map((response, index) => {
                    const isPendingButNotNeeded =
                      selectedRequest.status !== RequestStatus.Pending && response.response === "pending";

                    let containerClass = styles.approvalResponseContainer;
                    if (response.response === "approved") {
                      containerClass += ` ${styles.approvalResponseApproved}`;
                    } else if (response.response === "rejected") {
                      containerClass += ` ${styles.approvalResponseRejected}`;
                    } else if (isPendingButNotNeeded) {
                      containerClass += ` ${styles.approvalResponseNotNeeded}`;
                    } else {
                      containerClass += ` ${styles.approvalResponsePending}`;
                    }

                    return (
                      <div key={index} className={containerClass}>
                        <div className={styles.approvalResponderInfo}>
                          <div className={styles.approvalResponderName}>
                            <span style={{ fontSize: "18px" }}>
                              {response.response === "approved"
                                ? "✅"
                                : response.response === "rejected"
                                  ? "❌"
                                  : isPendingButNotNeeded
                                    ? "ℹ️"
                                    : "⏳"}
                            </span>
                            <strong>{response.approverName || response.approverEmail}</strong>
                          </div>
                          <div className={styles.approvalResponderDetails}>
                            <div>
                              {response.response === "approved"
                                ? t("modal.approvalsSection.responses.approved")
                                : response.response === "rejected"
                                  ? t("modal.approvalsSection.responses.rejected")
                                  : isPendingButNotNeeded
                                    ? t("modal.approvalsSection.responses.notNeeded")
                                    : t("modal.approvalsSection.responses.pending")}
                              {response.responseDate &&
                                ` • ${new Date(response.responseDate).toLocaleString("fr-FR")}`}
                            </div>
                            {response.comments && (
                              <div className={styles.approvalResponderComments}>
                                "{response.comments}"
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isApprover && selectedRequest?.status === RequestStatus.Pending && (() => {
              const userResponse = selectedRequest.approvalResponses?.find(
                (r) => r.approverId === currentUserId
              );
              const hasAlreadyResponded = userResponse && userResponse.response !== "pending";

              if (hasAlreadyResponded) {
                const userResponseClass = `${styles.approvalResponseContainer} ${
                  userResponse.response === "approved"
                    ? styles.approvalResponseApproved
                    : styles.approvalResponseRejected
                }`;

                return (
                  <div className={styles.approvalSection}>
                    <div className={styles.approvalTitle}>{t("modal.yourResponse.title")}</div>
                    <div className={userResponseClass}>
                      <div className={styles.approvalResponderInfo}>
                        <div className={styles.approvalResponderName}>
                          <span style={{ fontSize: "18px" }}>
                            {userResponse.response === "approved" ? "✅" : "❌"}
                          </span>
                          <strong>
                            {userResponse.response === "approved"
                              ? t("modal.yourResponse.approved")
                              : t("modal.yourResponse.rejected")}
                          </strong>
                        </div>
                        {userResponse.responseDate && (
                          <div className={styles.approvalResponderDetails}>
                            {new Date(userResponse.responseDate).toLocaleString("fr-FR")}
                          </div>
                        )}
                        {userResponse.comments && (
                          <div className={styles.approvalResponderComments}>
                            "{userResponse.comments}"
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className={styles.approvalSection}>
                  <div className={styles.approvalTitle}>{t("modal.approvalActions.title")}</div>
                  <div className={styles.commentInput}>
                    <label className={styles.commentLabel}>
                      {t("modal.approvalActions.commentsLabel")}
                    </label>
                    <Textarea
                      value={comments}
                      onChange={(_, data) => onCommentsChange(data?.value || "")}
                      placeholder={t("modal.approvalActions.commentsPlaceholder")}
                      rows={3}
                      className={styles.commentTextarea}
                    />
                  </div>
                </div>
              );
            })()}
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button
              appearance="secondary"
              onClick={onClose}
              className={styles.dialogButton}
            >
              {t("modal.buttons.close")}
            </Button>
            {isApprover && selectedRequest?.status === RequestStatus.Pending && (() => {
              const userResponse = selectedRequest.approvalResponses?.find(
                (r) => r.approverId === currentUserId
              );
              const hasAlreadyResponded = userResponse && userResponse.response !== "pending";

              if (hasAlreadyResponded) {
                return null;
              }

              return (
                <>
                  <Button
                    appearance="secondary"
                    onClick={onReject}
                    disabled={processingAction !== null}
                    className={styles.rejectButton}
                    icon={processingAction === "reject" ? <Spinner size="tiny" /> : undefined}
                  >
                    {processingAction === "reject"
                      ? t("modal.buttons.processing")
                      : t("modal.buttons.reject")}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={onApprove}
                    disabled={processingAction !== null}
                    className={styles.approveButton}
                    icon={processingAction === "approve" ? <Spinner size="tiny" /> : undefined}
                  >
                    {processingAction === "approve"
                      ? t("modal.buttons.processing")
                      : t("modal.buttons.approve")}
                  </Button>
                </>
              );
            })()}
            {!isApprover &&
              selectedRequest?.requesterId === currentUserId &&
              selectedRequest?.status === RequestStatus.Pending && (
                <Button
                  appearance="secondary"
                  onClick={() => onCancelClick(selectedRequest.id!)}
                  disabled={processingAction !== null}
                  className={styles.cancelButton}
                >
                  {t("modal.buttons.cancelRequest")}
                </Button>
              )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
